/**
 * GammaThemesGallery — горизонтальная лента карточек-тем для Gamma.
 *
 * Показывается в режиме «Создать с нуля». Карточки:
 *   [Авто ✨]  [Тема1]  [Тема2]  …
 *
 * Взаимодействие:
 *   - Клик по теме → выбор + сохранение themeId в gammaThemeId
 *   - Клик «Авто» → themeId: undefined (Gamma подбирает сама)
 *
 * Для загрузки своей PPTX пользователь переключает toggle «Редактировать» —
 * отдельной карточки «+ Свой шаблон» нет, т.к. Gamma API не поддерживает
 * пользовательские шаблоны, а дублировать toggle сбивает с толку.
 *
 * Данные: GET /api/v1/presentations/gamma/themes. Кешируем результат на уровне
 * модуля (в рамках сессии — 1 запрос на всю жизнь страницы). Если бэк вернул
 * пустой список (нет ключа / timeout) — показываем только «Авто».
 */
import { useEffect, useRef, useState } from 'react'
import { listGammaThemes, type GammaTheme } from '../../../api/presentations'
import { getThemeGradient, isDarkTheme } from '../utils/gammaThemeColors'

// ── Featured themes (опциональная фильтрация) ───────────────
// Если в FEATURED_IDS есть элементы и бэк вернул >20 тем — показываем сначала
// featured, потом остальные. Список можно расширить по мере опыта эксплуатации.
const FEATURED_THEME_IDS: string[] = [
	'default-light',
	'default-dark',
	'consultant',
	'commons',
	'chisel',
	'blue-steel',
	'aurum',
	'coal',
	'gleam',
	'aurora',
	'breeze',
	'founder',
	'dawn',
	'finesse',
]

// Module-level кеш: один fetch на всю сессию. Достаточно — список тем Gamma
// меняется редко; на бэке тоже есть кеш 1 час. Если пользователь загрузит
// страницу дважды за сессию — не бежим в сеть.
let _cachedThemes: GammaTheme[] | null = null
let _inflight: Promise<GammaTheme[]> | null = null

async function fetchThemesCached(): Promise<GammaTheme[]> {
	if (_cachedThemes) return _cachedThemes
	if (_inflight) return _inflight
	_inflight = listGammaThemes()
		.then((themes) => {
			_cachedThemes = themes
			return themes
		})
		.catch(() => {
			_cachedThemes = []
			return []
		})
		.finally(() => {
			_inflight = null
		})
	return _inflight
}

function themeId(theme: GammaTheme): string {
	return String(theme.themeId || theme.id || theme.name || '')
}

function orderThemes(all: GammaTheme[]): GammaTheme[] {
	if (all.length <= 20) return all
	// featured first, затем остальные
	const featured: GammaTheme[] = []
	const rest: GammaTheme[] = []
	for (const t of all) {
		const id = themeId(t)
		if (FEATURED_THEME_IDS.includes(id)) featured.push(t)
		else rest.push(t)
	}
	// Сохраняем порядок FEATURED_THEME_IDS
	featured.sort(
		(a, b) =>
			FEATURED_THEME_IDS.indexOf(themeId(a)) - FEATURED_THEME_IDS.indexOf(themeId(b)),
	)
	return [...featured, ...rest]
}

interface Props {
	selectedThemeId?: string
	onSelectTheme: (themeId: string | undefined, themeName?: string) => void
	disabled?: boolean
}

export default function GammaThemesGallery({
	selectedThemeId,
	onSelectTheme,
	disabled,
}: Props) {
	const [themes, setThemes] = useState<GammaTheme[] | null>(_cachedThemes)
	const [loading, setLoading] = useState(_cachedThemes === null)
	const mountedRef = useRef(true)

	useEffect(() => {
		mountedRef.current = true
		if (_cachedThemes) {
			setThemes(_cachedThemes)
			setLoading(false)
			return
		}
		fetchThemesCached().then((ts) => {
			if (!mountedRef.current) return
			setThemes(ts)
			setLoading(false)
		})
		return () => {
			mountedRef.current = false
		}
	}, [])

	const orderedThemes = themes ? orderThemes(themes) : []
	const isAutoSelected = !selectedThemeId

	return (
		<div className="gamma-themes">
			<div className="gamma-themes__title">Тема оформления</div>
			<div className="gamma-themes__strip">
				{/* Авто — первая карточка */}
				<ThemeCard
					label="Авто"
					hint="✨"
					isAuto
					selected={isAutoSelected}
					disabled={disabled}
					onClick={() => onSelectTheme(undefined, undefined)}
				/>

				{loading && <ThemeCardsSkeleton count={6} />}

				{!loading &&
					orderedThemes.map((theme) => {
						const id = themeId(theme)
						const gradient = getThemeGradient(theme.colorKeywords)
						const dark = isDarkTheme(theme.colorKeywords)
						return (
							<ThemeCard
								key={id}
								label={theme.name || id}
								gradient={gradient}
								darkText={!dark}
								selected={selectedThemeId === id}
								disabled={disabled}
								onClick={() => onSelectTheme(id, theme.name || id)}
							/>
						)
					})}
			</div>
		</div>
	)
}

// ── ThemeCard ─────────────────────────────────────────────────────

interface CardProps {
	label: string
	hint?: string
	gradient?: string
	darkText?: boolean
	isAuto?: boolean
	selected?: boolean
	disabled?: boolean
	onClick: () => void
}

function ThemeCard({
	label,
	hint,
	gradient,
	darkText,
	isAuto,
	selected,
	disabled,
	onClick,
}: CardProps) {
	const classes = [
		'gamma-themes__card',
		selected && 'gamma-themes__card--selected',
		isAuto && 'gamma-themes__card--auto',
		disabled && 'gamma-themes__card--disabled',
	]
		.filter(Boolean)
		.join(' ')

	const bgStyle = gradient ? { background: gradient } : undefined

	return (
		<button
			type="button"
			className={classes}
			style={bgStyle}
			onClick={onClick}
			disabled={disabled}
			title={label}
			aria-pressed={selected}
		>
			{hint && <span className="gamma-themes__card-hint">{hint}</span>}
			<span
				className={
					'gamma-themes__card-label' +
					(darkText ? ' gamma-themes__card-label--dark' : '')
				}
			>
				{label}
			</span>
		</button>
	)
}

function ThemeCardsSkeleton({ count }: { count: number }) {
	return (
		<>
			{Array.from({ length: count }).map((_, i) => (
				<div key={i} className="gamma-themes__card gamma-themes__card--skeleton" />
			))}
		</>
	)
}
